import { FormPartComponent, AgreementCheckbox as AgreementCheckboxType } from "./FormParts";
import { useEffect } from "react";
import { useForm } from "@mantine/form";
import { shopVar } from "@/lib/shopStyles";

function isSafeUrl(url: string): boolean {
    try {
        const parsed = new URL(url, window.location.origin);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
        return false;
    }
}

export function AgreementCheckbox({ formPart, onValueChange, onValidStatusChange, answers, errors }: FormPartComponent<AgreementCheckboxType>) {
    const form = useForm({
        mode: 'controlled',
        initialValues: {
            checked: typeof answers === 'string' && answers === "true",
        },
        validateInputOnChange: ['checked'],
        validate: {
            checked: (value: boolean) => {
                if (formPart.required && !value) {
                    return `${formPart.label} is required.`;
                }
                return null;
            },
        },
        onValuesChange: (values) => {
            onValueChange(formPart.key, values.checked ? "true" : "false");
            onValidStatusChange(formPart.key, form.isValid());
        }
    });

    useEffect(() => {
        onValidStatusChange(formPart.key, form.isValid());
    }, []);

    const serverErrors = Array.isArray(errors) ? errors.flat() : [];

    const hasDescription = !!formPart.description;
    const descriptionId = `${formPart.key}-description`;

    const renderLabel = () => {
        const { label, linkUrl, linkText } = formPart;

        if (linkUrl && linkText && isSafeUrl(linkUrl) && label.includes(linkText)) {
            const linkIndex = label.indexOf(linkText);
            const before = label.substring(0, linkIndex);
            const after = label.substring(linkIndex + linkText.length);

            return (
                <>
                    {before}
                    <a href={linkUrl} className="font-bold" target="_blank" rel="noopener noreferrer">{linkText}</a>
                    {after}
                </>
            );
        }

        return (
            <>
                {label}
                {linkUrl && linkText && isSafeUrl(linkUrl) && (
                    <>
                        {' '}
                        <a href={linkUrl} className="font-bold" target="_blank" rel="noopener noreferrer">{linkText}</a>
                    </>
                )}
            </>
        );
    };

    return (
        <div className="relative flex items-start pt-6" data-form-part={formPart.key}>
            <div className="flex h-6 items-center">
                <input
                    id={`agreement-${formPart.key}`}
                    name={formPart.key}
                    type="checkbox"
                    {...(hasDescription ? { 'aria-describedby': descriptionId } : {})}
                    className="h-4 w-4 rounded border-gray-300 focus:ring-indigo-600"
                    style={{
                        color: '#222222',
                    }}
                    {...form.getInputProps('checked', { type: 'checkbox' })}
                />
            </div>
            <div className="ml-3 text-sm leading-6">
                <label htmlFor={`agreement-${formPart.key}`} className="font-medium" style={{
                    color: shopVar('--shop-textColor'),
                }}>
                    {renderLabel()}
                    {formPart.required && <span className="text-red-600"> *</span>}
                </label>
                {hasDescription && (
                    <p id={descriptionId} className="text-gray-500">{formPart.description}</p>
                )}
                {form.errors.checked && <p className="text-sm text-red-600">{form.errors.checked}</p>}
                {serverErrors.length > 0 && serverErrors.map((error, i) => (
                    <p key={i} className="text-sm text-red-600">{error}</p>
                ))}
            </div>
        </div>
    );
}
